import { ru } from './ru';
import { en } from './en';
import enStrings from './en.json';

type Leaves<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string ? `${P}${K}` : Leaves<T[K], `${P}${K}.`>;
}[keyof T & string];

export type Dict = typeof ru;
export type TKey = Leaves<Dict>;
export type Locale = 'ru' | 'en';
export const LOCALES: Array<{ value: Locale; label: string }> = [
  { value: 'ru', label: 'Русский' },
  { value: 'en', label: 'English' },
];

const dictionaries: Record<Locale, unknown> = { ru, en };
const STORAGE_KEY = 'lumo-locale';

/** Язык: выбранный в интерфейсе, иначе язык браузера (русский — для ru/uk/be/kk). */
function detectLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'ru' || saved === 'en') return saved;
  } catch {
    /* ignore */
  }
  const langs = typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : ['ru'];
  return langs.some((l) => /^(ru|uk|be|kk)/i.test(l)) ? 'ru' : 'en';
}

let current: Locale = typeof window !== 'undefined' ? detectLocale() : 'ru';
if (typeof document !== 'undefined') document.documentElement.lang = current;

export function getLocale(): Locale {
  return current;
}

/**
 * Смена языка. Тексты читаются при отрисовке и в константах модулей,
 * поэтому страница перезагружается — так меняется всё сразу и без «смеси».
 */
export function setLocale(locale: Locale, reload = true): void {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* ignore */
  }
  if (locale === current) return;
  if (reload) location.reload();
  else {
    current = locale;
    document.documentElement.lang = locale;
  }
}

const enMap = enStrings as Record<string, string>;
/** Шаблоны с числами: «Через 5 мин» → ключ «Через {n} мин». */
const enPatterns: Array<[RegExp, string]> = Object.entries(enMap)
  .filter(([k]) => k.includes('{n}'))
  .map(([k, v]) => [new RegExp('^' + k.replace(/[.*+?^$()|[\]\\]/g, '\\$&').replace(/\{n\}/g, '(.+?)') + '$'), v]);

/**
 * Перевод строки интерфейса: русский текст — сам ключ, английский — в en.json.
 * Нет перевода — показываем как есть (лучше русский, чем пусто).
 */
export function tr(text: string): string {
  if (current === 'ru' || !text) return text;
  const hit = enMap[text];
  if (hit !== undefined) return hit;
  for (const [re, out] of enPatterns) {
    const m = re.exec(text);
    if (m) {
      let i = 1;
      return out.replace(/\{n\}/g, () => m[i++] ?? '');
    }
  }
  if (import.meta.env.DEV) missing.add(text);
  return text;
}

/** Непереведённые строки (в dev: window.__lumoMissing()). */
const missing = new Set<string>();
/**
 * Перевод шаблона с подстановками: trf('Решено · {name}', { name }).
 * Шаблон переводится целиком (порядок слов в языках разный), значения — после.
 */
export function trf(template: string, vars: Record<string, string | number>): string {
  return tr(template).replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? ''));
}

/** Число + форма слова по правилам текущего языка: trp(5, ['доска', 'доски', 'досок']). */
export function trp(n: number, forms: [string, string, string]): string {
  const cat = new Intl.PluralRules(current).select(n);
  const form = current === 'ru' ? forms[cat === 'one' ? 0 : cat === 'few' ? 1 : 2] : tr(forms[cat === 'one' ? 0 : 2]);
  return `${formatNumber(n)} ${form}`;
}

/** Дата коротко на языке интерфейса. */
export function formatDate(d: Date | number | string, opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }): string {
  return new Intl.DateTimeFormat(current, opts).format(new Date(d));
}

if (import.meta.env.DEV && typeof window !== 'undefined') (window as unknown as { __lumoMissing: () => string[] }).__lumoMissing = () => [...missing];

/**
 * Перевод по ключу с подстановкой `{name}`.
 * Плюрализация: `{count|стикер|стикера|стикеров}`.
 */
export function t(key: TKey, vars?: Record<string, string | number>): string {
  let node: unknown = dictionaries[current];
  for (const part of key.split('.')) node = (node as Record<string, unknown> | undefined)?.[part];
  let str = typeof node === 'string' ? node : key;
  if (vars) {
    str = str.replace(/\{(\w+)\|([^}]+)\}/g, (_, name: string, forms: string) => plural(Number(vars[name] ?? 0), forms.split('|')));
    str = str.replace(/\{(\w+)\}/g, (_, name: string) => String(vars[name] ?? ''));
  }
  return str;
}

function plural(n: number, forms: string[]): string {
  const cat = new Intl.PluralRules(current).select(n);
  // ru: one/few/many; en: one/other — формы в словаре en заданы как «one|other|other».
  const idx = cat === 'one' ? 0 : cat === 'few' ? 1 : 2;
  return `${n} ${forms[Math.min(idx, forms.length - 1)]}`;
}

export function formatNumber(n: number): string {
  return new Intl.NumberFormat(current).format(n);
}

const rtf = () => new Intl.RelativeTimeFormat(current, { numeric: 'auto' });
export function timeAgo(date: Date | number | string): string {
  const ms = new Date(date).getTime() - Date.now();
  const s = Math.round(ms / 1000);
  const abs = Math.abs(s);
  if (abs < 45) return t('time.justNow');
  if (abs < 3600) return rtf().format(Math.round(s / 60), 'minute');
  if (abs < 86400) return rtf().format(Math.round(s / 3600), 'hour');
  if (abs < 86400 * 30) return rtf().format(Math.round(s / 86400), 'day');
  return new Intl.DateTimeFormat(current, { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(date));
}
